import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Calendar, CalendarX2, Plus,
  CheckCircle2, Clock, AlertCircle, HardDrive, FileText, Upload,
  ClipboardList, User, HardHat, MapPin, Phone, Tag,
} from 'lucide-react';
import { toast } from 'sonner';
import type { AxiosError } from 'axios';
import type { SellerRegistrationStatus } from '@m8-app/contracts';
import { getSellerDevice, getServiceRequests, resolveServiceRequest, getSellerInvoice, replaceSellerInvoice } from '../api/seller.js';

const MAX_INVOICE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_MIMES = ['application/pdf', 'image/jpeg', 'image/png'] as const;
const ACCEPT_ATTR = '.pdf,.jpg,.jpeg,.png';

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}
import { StatusBadge } from '../components/ui/Badge.js';
import { DetailSkeleton } from '../components/ui/Skeleton.js';
import { CopyButton } from '../components/ui/CopyButton.js';
import { Button } from '../components/ui/Button.js';
import { ServiceModal } from '../components/ui/ServiceModal.js';
import { inferProductType } from '../lib/product.js';
import { ProductBadge } from '../lib/ProductBadge.js';

const STATUS_DESC: Record<SellerRegistrationStatus, string> = {
  pending:    'Registered by seller — awaiting customer claim.',
  activated:  'Device is active and live.',
  claimed:    'Customer has successfully claimed this device.',
  superseded: 'This registration has been superseded by a newer one.',
};

function InfoRow({ label, icon: Icon, children }: { label: string; icon?: React.ElementType; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between px-5 py-3.5 gap-4">
      <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 uppercase tracking-wide shrink-0 mt-0.5">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        {label}
      </span>
      <div className="flex items-center gap-1.5 min-w-0 text-right">{children}</div>
    </div>
  );
}

function SectionHeader({ icon: Icon, label }: { icon: React.ElementType; label: string }) {
  return (
    <div className="flex items-center gap-2 mb-3 mt-6">
      <Icon className="w-4 h-4 text-blue-600" />
      <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wide">{label}</h2>
    </div>
  );
}

export function DeviceDetailsPage() {
  const { id }   = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  const invoiceInputRef = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['seller-device', id],
    queryFn:  () => getSellerDevice(id!),
    enabled:  !!id,
  });

  const { data: serviceData } = useQuery({
    queryKey: ['service', id],
    queryFn:  () => getServiceRequests(id!),
    enabled:  !!id && !!data,
  });

  const resolveMutation = useMutation({
    mutationFn: (requestId: string) => resolveServiceRequest(requestId),
    onSuccess: () => {
      toast.success('Marked as resolved');
      qc.invalidateQueries({ queryKey: ['service', id] });
      qc.invalidateQueries({ queryKey: ['seller-services'] });
      setResolvingId(null);
    },
    onError: () => {
      toast.error('Failed to resolve request');
      setResolvingId(null);
    },
  });

  useEffect(() => {
    if (isError) {
      const msg = (error as AxiosError<{ message?: string }>)?.response?.data?.message;
      toast.error(msg ?? 'Failed to load device details.');
    }
  }, [isError, error]);

  const reg = data?.registration;

  async function handleViewInvoice() {
    if (!id) return;
    setDownloadingInvoice(true);
    try {
      const blob = await getSellerInvoice(id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch {
      toast.error('Invoice not available for this registration.');
    } finally {
      setDownloadingInvoice(false);
    }
  }

  const replaceInvoiceMutation = useMutation({
    mutationFn: (file: File) =>
      fileToBase64(file).then((base64) =>
        replaceSellerInvoice(id!, { base64, filename: file.name, mimeType: file.type as (typeof ACCEPTED_MIMES)[number] }),
      ),
    onSuccess: () => {
      toast.success('Invoice replaced.');
      qc.invalidateQueries({ queryKey: ['seller-device', id] });
    },
    onError: (err) => {
      const msg = (err as AxiosError<{ message?: string }>)?.response?.data?.message;
      toast.error(msg ?? 'Failed to replace invoice.');
    },
  });

  function handleInvoicePick(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (!ACCEPTED_MIMES.includes(f.type as (typeof ACCEPTED_MIMES)[number])) {
      toast.error('Invoice must be a PDF, JPG, or PNG file.');
      return;
    }
    if (f.size > MAX_INVOICE_BYTES) {
      toast.error('Invoice must be 5 MB or smaller.');
      return;
    }
    replaceInvoiceMutation.mutate(f);
  }

  const formattedDate = reg
    ? new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(reg.registeredAt))
    : '';

  const expiryInfo = reg
    ? (() => {
        // Prefer backend-computed warranty_expires_at (respects current admin
        // config; for M8 uses activation timestamp not seller registration).
        // Fall back to 2 years from warrantyStartAt/registeredAt for legacy
        // rows that predate the warranty_expires_at column.
        const d = reg.warrantyExpiresAt
          ? new Date(reg.warrantyExpiresAt)
          : (() => {
              const fallback = new Date(reg.warrantyStartAt ?? reg.registeredAt);
              fallback.setFullYear(fallback.getFullYear() + 2);
              return fallback;
            })();
        const isExpired = d < new Date();
        const formatted = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }).format(d);
        return { formatted, isExpired };
      })()
    : null;

  /* build a unified history timeline */
  type TimelineEvent =
    | { kind: 'registration'; date: string }
    | { kind: 'service'; id: string; problem: string; status: 'open' | 'resolved'; date: string };

  const timeline: TimelineEvent[] = reg
    ? ([
        { kind: 'registration' as const, date: reg.registeredAt },
        ...(serviceData?.requests ?? []).map((r) => ({
          kind: 'service' as const,
          id: r.id,
          problem: r.problem,
          status: r.status as 'open' | 'resolved',
          date: r.createdAt,
        })),
      ] as TimelineEvent[]).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    : [];

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.18 }}
      className="p-6 max-w-3xl"
    >
      <button type="button" onClick={() => navigate(-1)}
        className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800 transition-colors mb-5 -ml-0.5">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <h1 className="text-xl font-bold text-slate-900 mb-1">Device History</h1>
      <p className="text-sm text-slate-400 mb-6">Full record for this device — registration, users, installer and service events.</p>

      {isLoading && <DetailSkeleton rows={5} />}

      {isError && !isLoading && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-5 py-4 text-sm text-red-700">
          Could not load device details.
          <Button type="button" variant="ghost" size="sm" onClick={() => navigate(-1)} className="mt-2 text-red-700">Go back</Button>
        </div>
      )}

      {reg && (
        <>
          {/* ── Device identity card ── */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
            {/* Hero */}
            <div className="px-5 py-4 bg-gradient-to-r from-slate-50 to-white border-b border-slate-100">
              <div className="flex items-start gap-3.5">
                <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 shadow-sm flex items-center justify-center shrink-0">
                  <HardDrive className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-mono text-base font-bold text-slate-900 leading-tight">{reg.serialNumber}</p>
                      <p className="text-sm text-slate-500 mt-0.5">{reg.customerMobile}</p>
                    </div>
                    <StatusBadge status={reg.status} />
                  </div>
                  <p className="text-xs text-slate-400 mt-1.5">{STATUS_DESC[reg.status]}</p>
                </div>
              </div>
            </div>

            {/* Device info rows */}
            <div className="divide-y divide-slate-50">
              <InfoRow label="Product Type" icon={Tag}>
                <ProductBadge type={inferProductType(reg.serialNumber)} />
              </InfoRow>
              <InfoRow label="Registration ID" icon={ClipboardList}>
                <span className="font-mono text-xs text-slate-700 break-all">{reg.registrationId}</span>
                <CopyButton value={reg.registrationId} />
              </InfoRow>
              <InfoRow label="Serial Number" icon={HardDrive}>
                <span className="font-mono text-sm font-semibold text-slate-800">{reg.serialNumber}</span>
                <CopyButton value={reg.serialNumber} />
              </InfoRow>
            </div>
          </div>

          {/* ── Customer section ── */}
          <SectionHeader icon={User} label="Customer" />
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm divide-y divide-slate-50">
            <InfoRow label="Name" icon={User}>
              {reg.customerName
                ? <span className="text-sm text-slate-700">{reg.customerName}</span>
                : <span className="text-sm text-slate-400 italic">—</span>}
            </InfoRow>
            <InfoRow label="Mobile" icon={Phone}>
              <span className="text-sm text-slate-700">{reg.customerMobile}</span>
              <CopyButton value={reg.customerMobile} />
            </InfoRow>
            <InfoRow label="Address" icon={MapPin}>
              {reg.customerAddress
                ? <span className="text-sm text-slate-700 text-right">{reg.customerAddress}</span>
                : <span className="text-sm text-slate-400 italic">—</span>}
            </InfoRow>
            <InfoRow label="Users on device">
              <button
                type="button"
                onClick={() => navigate(`/users?device=${reg.registrationId}&serial=${encodeURIComponent(reg.serialNumber)}`)}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-800 hover:underline underline-offset-2 transition-colors"
              >
                View user list →
              </button>
            </InfoRow>
          </div>

          {/* ── Installer section ── */}
          <SectionHeader icon={HardHat} label="Installer" />
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm divide-y divide-slate-50">
            <InfoRow label="Name" icon={User}>
              {reg.installerName
                ? <span className="text-sm text-slate-700">{reg.installerName}</span>
                : <span className="text-sm text-slate-400 italic">—</span>}
            </InfoRow>
            <InfoRow label="Mobile" icon={Phone}>
              {reg.installerMobile
                ? <><span className="text-sm text-slate-700">{reg.installerMobile}</span><CopyButton value={reg.installerMobile} /></>
                : <span className="text-sm text-slate-400 italic">—</span>}
            </InfoRow>
            {reg.installerMobile && (
              <InfoRow label="Installer profile">
                <button
                  type="button"
                  onClick={() => navigate(`/installers?name=${encodeURIComponent(reg.installerName ?? '')}`)}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-800 hover:underline underline-offset-2 transition-colors"
                >
                  View installer →
                </button>
              </InfoRow>
            )}
          </div>

          {/* ── Warranty ── */}
          <SectionHeader icon={Calendar} label="Warranty" />
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm divide-y divide-slate-50">
            <InfoRow label="Registered" icon={Calendar}>
              <span className="text-sm text-slate-700">{formattedDate}</span>
            </InfoRow>
            {expiryInfo && (
              <InfoRow label="Warranty Expiry" icon={CalendarX2}>
                <span className={`text-sm ${expiryInfo.isExpired ? 'text-red-600 font-semibold' : 'text-slate-700'}`}>
                  {expiryInfo.formatted}
                </span>
                {expiryInfo.isExpired && (
                  <span className="text-xs font-medium bg-red-50 ring-1 ring-red-200/60 text-red-600 px-1.5 py-0.5 rounded ml-1">Expired</span>
                )}
              </InfoRow>
            )}
            <InfoRow label="Invoice" icon={FileText}>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                {reg.invoiceFilename ? (
                  <>
                    <span className="text-xs text-slate-500 truncate max-w-[140px]" title={reg.invoiceFilename}>
                      {reg.invoiceFilename}
                    </span>
                    <button
                      type="button"
                      disabled={downloadingInvoice}
                      onClick={handleViewInvoice}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-50 transition-colors"
                    >
                      <FileText className="w-3.5 h-3.5" />
                      {downloadingInvoice ? 'Opening…' : 'View'}
                    </button>
                  </>
                ) : (
                  <span className="text-xs text-slate-400 italic">No invoice on file</span>
                )}
                <button
                  type="button"
                  disabled={replaceInvoiceMutation.isPending}
                  onClick={() => invoiceInputRef.current?.click()}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-50 transition-colors"
                >
                  <Upload className="w-3.5 h-3.5" />
                  {replaceInvoiceMutation.isPending
                    ? 'Uploading…'
                    : reg.invoiceFilename ? 'Replace' : 'Upload'}
                </button>
                <input
                  ref={invoiceInputRef}
                  type="file"
                  accept={ACCEPT_ATTR}
                  className="hidden"
                  onChange={handleInvoicePick}
                />
              </div>
            </InfoRow>
          </div>

          {/* ── Full History Timeline ── */}
          <div className="flex items-center justify-between mb-3 mt-6">
            <div className="flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-blue-600" />
              <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wide">History Timeline</h2>
            </div>
            <button type="button" onClick={() => setServiceModalOpen(true)}
              className="flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-700 border border-blue-200 hover:border-blue-300 px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-all">
              <Plus className="w-3.5 h-3.5" /> Log Service Request
            </button>
          </div>

          {timeline.length === 0 ? (
            <div className="bg-slate-50 border border-dashed border-slate-200 rounded-xl px-6 py-8 text-center">
              <AlertCircle className="w-5 h-5 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-400">No events yet.</p>
            </div>
          ) : (
            <div className="relative">
              <div className="absolute left-[18px] top-5 bottom-5 w-px bg-slate-100" />
              <div className="space-y-3">
                {timeline.map((ev) => {
                  if (ev.kind === 'registration') {
                    return (
                      <div key="reg-event" className="flex gap-3">
                        <div className="shrink-0 mt-3.5 z-10">
                          <div className="w-5 h-5 rounded-full bg-blue-100 ring-2 ring-white flex items-center justify-center">
                            <HardDrive className="w-3 h-3 text-blue-600" />
                          </div>
                        </div>
                        <div className="flex-1 bg-white border border-slate-200 rounded-xl px-4 py-3 shadow-sm">
                          <p className="text-sm font-semibold text-slate-800">Device Registered</p>
                          <p className="text-xs text-slate-500 mt-0.5">
                            Serial <span className="font-mono">{reg.serialNumber}</span> registered by seller.
                          </p>
                          <p className="text-xs text-slate-400 mt-1.5">
                            {new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(ev.date))}
                          </p>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={ev.id} className="flex gap-3">
                      <div className="shrink-0 mt-3.5 z-10">
                        {ev.status === 'resolved'
                          ? <div className="w-5 h-5 rounded-full bg-emerald-100 ring-2 ring-white flex items-center justify-center"><CheckCircle2 className="w-3 h-3 text-emerald-600" /></div>
                          : <div className="w-5 h-5 rounded-full bg-amber-100 ring-2 ring-white flex items-center justify-center"><Clock className="w-3 h-3 text-amber-600" /></div>
                        }
                      </div>
                      <div className="flex-1 bg-white border border-slate-200 rounded-xl px-4 py-3 shadow-sm">
                        <div className="flex items-start justify-between gap-3 mb-1.5">
                          <div className="flex-1">
                            <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-0.5">Service Request</p>
                            <p className="text-sm text-slate-700 leading-relaxed">{ev.problem}</p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-md ring-1 ${
                              ev.status === 'resolved'
                                ? 'bg-emerald-50 text-emerald-700 ring-emerald-200/60'
                                : 'bg-amber-50 text-amber-700 ring-amber-200/60'
                            }`}>
                              {ev.status === 'resolved' ? 'Resolved' : 'Open'}
                            </span>
                            {ev.status === 'open' && (
                              <button type="button"
                                onClick={() => { setResolvingId(ev.id); resolveMutation.mutate(ev.id); }}
                                disabled={resolvingId === ev.id}
                                className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 transition-colors">
                                <CheckCircle2 className="w-3 h-3" /> Resolve
                              </button>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-slate-400">
                          {new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(ev.date))}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {serviceModalOpen && (
            <ServiceModal registrationId={reg.registrationId} serialNumber={reg.serialNumber} onClose={() => setServiceModalOpen(false)} />
          )}
        </>
      )}
    </motion.div>
  );
}
